import { useCallback, useEffect, useState } from "react";
import { Alert, Box, Button, Chip, Grid, Snackbar, Stack, Typography } from "@mui/material";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import Header from "../../components/Header";
import GlassCard from "../../components/dashboard/GlassCard";
import saasApi from "../../utils/saasApi";
import AlertTypeBadge from "../../components/alerts/AlertTypeBadge";

const ManageFiles = () => {
  const [clips, setClips] = useState([]);
  const [selected, setSelected] = useState(null);
  const [publicLink, setPublicLink] = useState("");
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });

  const notify = useCallback((message, severity = "success") => {
    setSnack({ open: true, message, severity });
  }, []);

  const fetchClips = useCallback(async () => {
    try {
      setClips(await saasApi.getClips());
    } catch (err) {
      notify(err.message, "error");
    }
  }, [notify]);

  useEffect(() => {
    fetchClips();
  }, [fetchClips]);

  const clipUrl = (clip) => clip.public_url || clip.storage_key || "";
  const apiBase = (process.env.REACT_APP_API_BASE || "").replace(/\/v1\/api\/?$/, "").replace(/\/$/, "");
  const downloadUrl = (clip) => `${apiBase}/api/saas/clips/${clip.id}/download`;

  const createLink = async (clip) => {
    try {
      const payload = await saasApi.createClipLink(clip.id, { minutes: 60 });
      const url = `${apiBase}${payload.url}`;
      setPublicLink(url);
      notify("Link temporal generado por 60 minutos");
    } catch (err) {
      notify(err.message, "error");
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Header title="Evidencia" subtitle="Clips de alertas desde el storage SaaS unificado" />

      <Grid container spacing={3}>
        <Grid item xs={12} lg={selected ? 7 : 12}>
          <Grid container spacing={2}>
            {clips.map((clip) => (
              <Grid item xs={12} md={6} lg={4} key={clip.id}>
                <GlassCard hover={false}>
                  <Stack spacing={1.5}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
                      <Typography variant="h5">{clip.camera_name || "Camara"}</Typography>
                      <Chip label={clip.status} size="small" />
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                      {clip.branch_name || "Sucursal"}
                    </Typography>
                    <AlertTypeBadge type={clip.detection_type} label={clip.detection_type || "alerta"} />
                    <Typography variant="caption" color="text.secondary">
                      Vence: {clip.expires_at ? new Date(clip.expires_at).toLocaleDateString("es-AR") : "Sin fecha"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Retencion: {clip.retention_until ? new Date(clip.retention_until).toLocaleDateString("es-AR") : "30 dias"}
                    </Typography>
                    <Stack direction="row" gap={1}>
                      <Button size="small" variant="contained" startIcon={<PlayCircleOutlineIcon />} onClick={() => setSelected(clip)} disabled={!clipUrl(clip)}>
                        Ver
                      </Button>
                      <Button size="small" variant="outlined" startIcon={<DownloadOutlinedIcon />} component="a" href={downloadUrl(clip)} target="_blank" rel="noreferrer">
                        Descargar
                      </Button>
                      <Button size="small" variant="outlined" onClick={() => createLink(clip)}>
                        Link 60m
                      </Button>
                    </Stack>
                  </Stack>
                </GlassCard>
              </Grid>
            ))}
            {clips.length === 0 && (
              <Grid item xs={12}>
                <GlassCard hover={false}>
                  <Typography color="text.secondary">Todavia no hay clips disponibles.</Typography>
                </GlassCard>
              </Grid>
            )}
          </Grid>
        </Grid>

        {selected && (
          <Grid item xs={12} lg={5}>
            <GlassCard hover={false}>
              <Stack spacing={2}>
                <Typography variant="h5">Reproductor</Typography>
                <Box sx={{ bgcolor: "#000", borderRadius: "8px", overflow: "hidden" }}>
                  <video src={clipUrl(selected)} controls style={{ width: "100%", display: "block", maxHeight: 420 }} />
                </Box>
                <Button onClick={() => setSelected(null)}>Cerrar reproductor</Button>
              </Stack>
            </GlassCard>
          </Grid>
        )}
      </Grid>

      {publicLink && (
        <GlassCard hover={false} sx={{ mt: 2 }}>
          <Typography variant="h6">Link publico temporal</Typography>
          <Typography sx={{ wordBreak: "break-all" }}>{publicLink}</Typography>
        </GlassCard>
      )}

      <Snackbar open={snack.open} autoHideDuration={4200} onClose={() => setSnack({ ...snack, open: false })}>
        <Alert severity={snack.severity}>{snack.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default ManageFiles;
